'use client';

import { ReactNode, useEffect, useState } from 'react';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import LoadingPlaceholder from './LoadingPlaceholder';
import { useExperimentService } from '@/hooks/experimentService';
import { EXPERIMENT_LAZY_LOAD } from '@/lib/constants/experiments';
import clsx from 'clsx';
interface LazyLoadSectionProps {
  children: ReactNode;
  fallback?: ReactNode;
  rootMargin?: string;
  threshold?: number | number[];
  className?: string;
  placeholderHeight?: string;
}

/**
 * This component is used to lazy load a section of the page.
 * It is used to improve the performance of the page by only loading the section when it is visible in the viewport.
 */
export default function LazyLoadSection({
  children,
  fallback,
  rootMargin = '200px',
  threshold = 0,
  className = '',
  placeholderHeight = 'h-32'
}: LazyLoadSectionProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const {
    ref,
    isIntersecting
  } = useIntersectionObserver({
    rootMargin,
    threshold
  });
  const {
    getExistingVariant
  } = useExperimentService();
  const experimentVariant = getExistingVariant(EXPERIMENT_LAZY_LOAD);
  const defaultFallback = <LoadingPlaceholder height={placeholderHeight} />;
  useEffect(() => {
    if (isIntersecting && !isLoaded) {
      setIsLoaded(true);
    }
  }, [isIntersecting, isLoaded]);
  if (!experimentVariant || experimentVariant === 'control') {
    return children;
  }
  return <div ref={ref} className={clsx(className, 'h-fit w-full overflow-hidden')} data-sentry-component="LazyLoadSection" data-sentry-source-file="LazyLoadSection.tsx">
      {isIntersecting || isLoaded ? children : fallback ?? defaultFallback}
    </div>;
}